/**
 * GlobalSearch Component
 * Search across clients, estimates, jobs, and invoices
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { apiHelpers } from '@/lib/api';
import { Input } from '@/components/ui/input';
import { 
  Search, 
  X, 
  Users, 
  FileText, 
  Calendar, 
  Receipt,
  Loader2,
  ArrowRight
} from 'lucide-react';
import { cn } from '@/lib/utils';

const GlobalSearch = ({ className }) => {
  const { organization } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ clients: [], estimates: [], jobs: [], invoices: [] });
  const [isLoading, setIsLoading] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const containerRef = useRef(null);
  const debounceRef = useRef(null);

  // Flatten results for keyboard navigation
  const flatResults = [
    ...results.clients.map(r => ({ ...r, type: 'client' })),
    ...results.estimates.map(r => ({ ...r, type: 'estimate' })),
    ...results.jobs.map(r => ({ ...r, type: 'job' })),
    ...results.invoices.map(r => ({ ...r, type: 'invoice' })),
  ];

  const totalResults = flatResults.length;

  // Search function
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim() || !organization?.id) {
      setResults({ clients: [], estimates: [], jobs: [], invoices: [] });
      return;
    }

    setIsLoading(true);
    try {
      const [clientsRes, estimatesRes, jobsRes, invoicesRes] = await Promise.all([
        apiHelpers.getClients(organization.id, searchQuery).catch(() => ({ data: [] })),
        apiHelpers.getEstimates(organization.id).catch(() => ({ data: [] })),
        apiHelpers.getJobs(organization.id).catch(() => ({ data: [] })),
        apiHelpers.getInvoices(organization.id).catch(() => ({ data: [] })),
      ]);

      // Filter results client-side for estimates, jobs, invoices
      const searchLower = searchQuery.toLowerCase();
      
      const filteredEstimates = (estimatesRes.data || [])
        .filter(e => 
          e.title?.toLowerCase().includes(searchLower) ||
          e.id?.toLowerCase().includes(searchLower) ||
          e.client_name?.toLowerCase().includes(searchLower)
        )
        .slice(0, 5);

      const filteredJobs = (jobsRes.data || [])
        .filter(j => 
          j.title?.toLowerCase().includes(searchLower) ||
          j.address?.toLowerCase().includes(searchLower) ||
          j.client?.name?.toLowerCase().includes(searchLower)
        )
        .slice(0, 5);

      const filteredInvoices = (invoicesRes.data || [])
        .filter(i => 
          i.id?.toLowerCase().includes(searchLower) ||
          i.client_name?.toLowerCase().includes(searchLower)
        )
        .slice(0, 5);

      setResults({
        clients: (clientsRes.data || []).slice(0, 5),
        estimates: filteredEstimates,
        jobs: filteredJobs,
        invoices: filteredInvoices,
      });
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setIsLoading(false);
    }
  }, [organization?.id]);

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    
    if (query.trim()) {
      debounceRef.current = setTimeout(() => {
        performSearch(query);
      }, 300);
    } else {
      setResults({ clients: [], estimates: [], jobs: [], invoices: [] });
    }

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [query, performSearch]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) {
        // Open search with Cmd/Ctrl + K
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
          e.preventDefault();
          setIsOpen(true);
          setTimeout(() => inputRef.current?.focus(), 100);
        }
        return;
      }

      switch (e.key) {
        case 'Escape':
          setIsOpen(false);
          setQuery('');
          break;
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => Math.min(prev + 1, totalResults - 1));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => Math.max(prev - 1, -1));
          break;
        case 'Enter':
          if (selectedIndex >= 0 && flatResults[selectedIndex]) {
            handleResultClick(flatResults[selectedIndex]);
          }
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, totalResults, flatResults]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleResultClick = (result) => {
    const routes = {
      client: `/clients/${result.id}`,
      estimate: `/estimates/${result.id}`,
      job: `/jobs?selected=${result.id}`,
      invoice: `/invoices/${result.id}`,
    };
    
    navigate(routes[result.type]);
    setIsOpen(false);
    setQuery('');
  };

  const getIcon = (type) => {
    const icons = {
      client: Users,
      estimate: FileText,
      job: Calendar,
      invoice: Receipt,
    };
    return icons[type] || FileText;
  };

  const getTypeLabel = (type) => {
    const labels = {
      client: 'Client',
      estimate: 'Estimate',
      job: 'Job',
      invoice: 'Invoice',
    };
    return labels[type];
  };

  const getResultTitle = (result) => {
    switch (result.type) {
      case 'client':
        return result.name;
      case 'estimate':
        return result.title || `Estimate #${result.id?.slice(0, 8).toUpperCase()}`;
      case 'job':
        return result.title || result.address || 'Job';
      case 'invoice':
        return `Invoice #${result.id?.slice(0, 8).toUpperCase()}`;
      default:
        return result.name || result.title || result.id;
    }
  };

  const getResultSubtitle = (result) => {
    switch (result.type) {
      case 'client':
        return result.email || result.phone;
      case 'estimate':
        return result.client_name || `$${result.total?.toLocaleString()}`;
      case 'job':
        return result.client?.name || result.status;
      case 'invoice':
        return result.client_name || `$${result.amount_due?.toLocaleString()}`;
      default:
        return '';
    }
  };

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      {/* Search Trigger */}
      <button
        onClick={() => {
          setIsOpen(true);
          setTimeout(() => inputRef.current?.focus(), 100);
        }}
        className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors w-full max-w-xs"
        data-testid="global-search-trigger"
      >
        <Search className="w-4 h-4" />
        <span className="flex-1 text-left">Search...</span>
        <kbd className="hidden sm:inline-flex items-center gap-1 px-1.5 py-0.5 text-xs font-medium bg-gray-200 rounded">
          ⌘K
        </kbd>
      </button>

      {/* Search Modal */}
      {isOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center pt-[15vh]">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-xl mx-4 overflow-hidden">
            {/* Search Input */}
            <div className="flex items-center gap-3 px-4 border-b">
              <Search className="w-5 h-5 text-muted-foreground flex-shrink-0" />
              <Input
                ref={inputRef}
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                  setSelectedIndex(-1);
                }}
                placeholder="Search clients, estimates, jobs, invoices..."
                className="border-0 focus-visible:ring-0 text-base py-4"
                data-testid="global-search-input"
              />
              {isLoading ? (
                <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
              ) : query && (
                <button onClick={() => setQuery('')} className="text-muted-foreground hover:text-foreground">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Results */}
            <div className="max-h-[50vh] overflow-y-auto">
              {totalResults === 0 && query.trim() && !isLoading ? (
                <div className="px-4 py-8 text-center text-muted-foreground">
                  <Search className="w-10 h-10 mx-auto mb-2 opacity-30" />
                  <p>No results found for "{query}"</p>
                </div>
              ) : (
                <div className="py-2">
                  {/* Clients */}
                  {results.clients.length > 0 && (
                    <div className="px-3 py-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase px-2 mb-1">Clients</p>
                      {results.clients.map((client, idx) => {
                        const flatIdx = idx;
                        return (
                          <ResultItem
                            key={client.id}
                            result={{ ...client, type: 'client' }}
                            isSelected={selectedIndex === flatIdx}
                            onClick={() => handleResultClick({ ...client, type: 'client' })}
                            getIcon={getIcon}
                            getResultTitle={getResultTitle}
                            getResultSubtitle={getResultSubtitle}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* Estimates */}
                  {results.estimates.length > 0 && (
                    <div className="px-3 py-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase px-2 mb-1">Estimates</p>
                      {results.estimates.map((estimate, idx) => {
                        const flatIdx = results.clients.length + idx;
                        return (
                          <ResultItem
                            key={estimate.id}
                            result={{ ...estimate, type: 'estimate' }}
                            isSelected={selectedIndex === flatIdx}
                            onClick={() => handleResultClick({ ...estimate, type: 'estimate' })}
                            getIcon={getIcon}
                            getResultTitle={getResultTitle}
                            getResultSubtitle={getResultSubtitle}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* Jobs */}
                  {results.jobs.length > 0 && (
                    <div className="px-3 py-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase px-2 mb-1">Jobs</p>
                      {results.jobs.map((job, idx) => {
                        const flatIdx = results.clients.length + results.estimates.length + idx;
                        return (
                          <ResultItem
                            key={job.id}
                            result={{ ...job, type: 'job' }}
                            isSelected={selectedIndex === flatIdx}
                            onClick={() => handleResultClick({ ...job, type: 'job' })}
                            getIcon={getIcon}
                            getResultTitle={getResultTitle}
                            getResultSubtitle={getResultSubtitle}
                          />
                        );
                      })}
                    </div>
                  )}

                  {/* Invoices */}
                  {results.invoices.length > 0 && (
                    <div className="px-3 py-2">
                      <p className="text-xs font-medium text-muted-foreground uppercase px-2 mb-1">Invoices</p>
                      {results.invoices.map((invoice, idx) => {
                        const flatIdx = results.clients.length + results.estimates.length + results.jobs.length + idx;
                        return (
                          <ResultItem
                            key={invoice.id}
                            result={{ ...invoice, type: 'invoice' }}
                            isSelected={selectedIndex === flatIdx}
                            onClick={() => handleResultClick({ ...invoice, type: 'invoice' })}
                            getIcon={getIcon}
                            getResultTitle={getResultTitle}
                            getResultSubtitle={getResultSubtitle}
                          />
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="px-4 py-2 border-t bg-gray-50 flex items-center justify-between text-xs text-muted-foreground">
              <div className="flex items-center gap-4">
                <span><kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↑↓</kbd> navigate</span>
                <span><kbd className="px-1.5 py-0.5 bg-gray-200 rounded">↵</kbd> select</span>
                <span><kbd className="px-1.5 py-0.5 bg-gray-200 rounded">esc</kbd> close</span>
              </div>
              {totalResults > 0 && (
                <span>{totalResults} result{totalResults !== 1 ? 's' : ''}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// Result Item Component
const ResultItem = ({ result, isSelected, onClick, getIcon, getResultTitle, getResultSubtitle }) => {
  const Icon = getIcon(result.type);
  
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors",
        isSelected ? "bg-blue-50 text-blue-900" : "hover:bg-gray-100"
      )}
      data-testid={`search-result-${result.id}`}
    >
      <div className={cn(
        "w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0",
        result.type === 'client' && "bg-purple-100 text-purple-600",
        result.type === 'estimate' && "bg-blue-100 text-blue-600",
        result.type === 'job' && "bg-green-100 text-green-600",
        result.type === 'invoice' && "bg-amber-100 text-amber-600",
      )}>
        <Icon className="w-4 h-4" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-medium truncate">{getResultTitle(result)}</p>
        <p className="text-sm text-muted-foreground truncate">{getResultSubtitle(result)}</p>
      </div>
      <ArrowRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100" />
    </button>
  );
};

export default GlobalSearch;
